import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';
import SimpleEntityList from '../DesignSystems/New/SimpleEntityList';
import React, { useMemo, useState } from 'react';
import { setCurrentSitrepId, setSelectedDChat } from '../../slices/dchatSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  useDeleteDisasterChat,
  useDisasterChats,
} from './hooks/useDisasterChats';
import DisasterChatEditDrawer from './DisasterChatEditDrawer';
import DeleteModal from '../DesignSystems/DeleteModal';
import { ListGroup, ListGroupItem } from 'react-bootstrap';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import ChatDisasterGPT from './ChatDisasterGPT';

type DisasterChatManagerProps = {
  selectedDatetime?: any;
  currentView?: 'chat' | 'list';
  setCurrentView?: (view: 'chat' | 'list') => void;
};
const DisasterChatManager = ({
  selectedDatetime,
  currentView: propCurrentView = 'chat',
  setCurrentView: propSetCurrentView,
}: DisasterChatManagerProps) => {
  const dispatch = useDispatch();

  const { data: chats = [], isLoading } = useDisasterChats();
  const deleteDisasterChat = useDeleteDisasterChat();

  // Get current selected chat from Redux
  const selectedChat = useSelector((state: any) => state.dchat.dchat);

  const [chatToEdit, setChatToEdit] = useState<any | undefined>();
  const [chatToDelete, setChatToDelete] = useState<any | undefined>();
  const [filterText, setFilterText] = useState('');
  // Use local state as fallback when props are not provided
  const [localCurrentView, setLocalCurrentView] = useState<'chat' | 'list'>(
    'chat'
  );

  const currentView = propSetCurrentView ? propCurrentView : localCurrentView;
  const setCurrentView = propSetCurrentView || setLocalCurrentView;

  const handleChatClick = (chat: any) => {
    dispatch(setCurrentSitrepId(undefined));
    dispatch(setSelectedDChat(chat));
    setCurrentView('chat'); // Switch to chat view
  };

  const handleAddChat = () => {
    // "Add" means start a new chat (empty chat object)
    handleChatClick({});
  };

  const handleToggleView = () => {
    setCurrentView(currentView === 'chat' ? 'list' : 'chat');
  };

  const handleChatClose = () => {
    // Close chat and return to list view
    dispatch(setSelectedDChat({}));
    dispatch(setCurrentSitrepId(undefined));
    setCurrentView('list');
  };

  const handleDeleteChat = (chat: any) => {
    setChatToDelete(chat);
  };

  const handleConfirmDelete = () => {
    if (chatToDelete) {
      deleteDisasterChat.mutate(chatToDelete.id, {
        onSettled: () => {
          setChatToDelete(undefined);
        },
      });
    }
  };

  const handleEditChat = (chat: any) => {
    setChatToEdit(chat);
  };

  const filteredChats = useMemo(() => {
    const lowerCaseFilter = filterText.toLowerCase();
    return chats.filter((chat: any) => {
      if (typeof chat.name === 'string') {
        return chat.name.toLowerCase().includes(lowerCaseFilter);
      } else {
        console.warn('Encountered chat with invalid name:', chat);
        return false;
      }
    });
  }, [chats, filterText]);

  return (
    <>
      {currentView === 'chat' ? (
        <div className="d-flex flex-column h-100">
          {/* Chat Header with List Button */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              margin: '8px',
              alignItems: 'center',
              borderBottom: '1px solid #dee2e6',
              paddingBottom: '8px',
            }}
          >
            <StylishNewButton
              className="button--secondary button--icon"
              onClick={handleToggleView}
              title="View chat list"
            >
              <SharedIcon iconName={'list'} size={'sm'} />
            </StylishNewButton>
            <h5 className="m-0" style={{ flexGrow: 1 }}>
              {selectedChat?.name || '(New chat)'}
            </h5>
          </div>

          {/* Chat Interface */}
          <div style={{ flexGrow: 1, overflow: 'hidden' }}>
            <ChatDisasterGPT
              selectedDatetime={selectedDatetime}
              toggle={handleChatClose}
            />
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column h-100">
          {/* List Header */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              margin: '8px',
              alignItems: 'center',
            }}
          >
            <StylishNewButton
              onClick={handleAddChat}
              className="button--primary"
            >
              Add
            </StylishNewButton>
            <div style={{ flexGrow: 1 }}>
              <StylishNewSearchBar
                value={filterText}
                onChangeSearchTerm={(val) => setFilterText(val)}
                placeholder="Filter chats by name"
              />
            </div>
          </div>

          {/* Chat List */}
          <div style={{ flexGrow: 1, overflowY: 'auto' }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <span>Loading...</span>
              </div>
            ) : filteredChats.length > 0 ? (
              <ListGroup className={'py-0'}>
                <SimpleEntityList
                  entities={filteredChats}
                  renderEntity={(chat) => (
                    <ListGroupItem
                      role="button"
                      tabIndex={0}
                      key={chat.id}
                      className="d-flex justify-content-between align-items-center py-3"
                      onClick={() => handleChatClick(chat)}
                      onKeyDown={(e) => {
                        // Prevent double-triggering when user presses Enter/Space on nested buttons
                        if (e.target instanceof HTMLButtonElement) {
                          return;
                        }
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleChatClick(chat);
                        }
                      }}
                    >
                      <div>
                        <strong title={chat.name}>
                          {chat.name.length > 50
                            ? `${chat.name.slice(0, 50)}...`
                            : chat.name}
                        </strong>
                      </div>
                      <div>
                        <StylishNewButton
                          className="button--secondary button--icon me-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditChat(chat);
                          }}
                        >
                          <SharedIcon iconName={'edit'} size={'sm'} />
                        </StylishNewButton>
                        <StylishNewButton
                          className="button--secondary button--icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChat(chat);
                          }}
                        >
                          <SharedIcon iconName={'delete'} size={'sm'} />
                        </StylishNewButton>
                      </div>
                    </ListGroupItem>
                  )}
                />
              </ListGroup>
            ) : (
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <span>No chats found.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {chatToEdit && (
        <DisasterChatEditDrawer
          show={!!chatToEdit}
          onHide={() => setChatToEdit(null)}
          chat={chatToEdit}
        />
      )}
      {chatToDelete ? (
        <DeleteModal
          title="Delete Session"
          onClose={() => setChatToDelete(undefined)}
          onConfirmDelete={handleConfirmDelete}
        >
          Delete this PRATUS AI Agent session?
        </DeleteModal>
      ) : null}
    </>
  );
};

export default DisasterChatManager;
